
import React, { useState, useEffect, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface EnhancedTableCellProps {
  value: any;
  field: string;
  rowData: any;
  editable?: boolean;
  inputType?: "text" | "number" | "textarea" | "select" | "url" | "date";
  selectOptions?: Array<{label: string, value: string}>;
  onChange?: (field: string, value: any) => void;
  isPending?: boolean;
  className?: string;
}

export const EnhancedTableCell: React.FC<EnhancedTableCellProps> = ({
  value,
  field,
  rowData,
  editable = false,
  inputType = "text",
  selectOptions = [],
  onChange,
  isPending = false,
  className
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (onChange && localValue !== value) {
      onChange(field, localValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputType !== 'textarea') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleClick = () => {
    if (editable && !isEditing) {
      setIsEditing(true);
    }
  };

  const formatDisplayValue = (val: any) => {
    if (val === null || val === undefined) return '';
    
    // Convert to string first
    const stringValue = val.toString();
    
    if (inputType === 'number') return stringValue;
    if (inputType === 'url' && stringValue) {
      // For URLs, show more characters but still truncate if very long
      return stringValue.length > 50 ? `${stringValue.substring(0, 50)}...` : stringValue;
    }
    if (inputType === 'date' && stringValue) {
      // Format date for display
      try {
        const date = new Date(stringValue);
        return date.toLocaleDateString();
      } catch {
        return stringValue;
      }
    }
    
    return stringValue;
  };

  if (!editable) {
    return (
      <div className={cn("py-2 px-3", className)}>
        {formatDisplayValue(value)}
      </div>
    );
  }

  if (inputType === 'select' && selectOptions.length > 0) {
    return (
      <div className={cn("relative", className)}>
        {isPending && (
          <div className="absolute -top-1 -right-1 z-10">
            <Badge variant="secondary" className="h-4 px-1 text-xs bg-yellow-100 text-yellow-800">
              <Loader2 className="h-2 w-2 animate-spin mr-1" />
              Saving
            </Badge>
          </div>
        )}
        <SearchableSelect
          options={selectOptions.map(opt => opt.value)}
          value={value || undefined}
          onChange={(newValue) => onChange?.(field, newValue)}
          placeholder="Select..."
          className="w-full"
        />
      </div>
    );
  }

  if (isEditing) {
    if (inputType === 'textarea') {
      return (
        <div className={cn("relative", className)}>
          <Textarea
            ref={textareaRef}
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleSave();
              }
              if (e.key === 'Escape') {
                handleCancel();
              }
            }}
            className="min-h-[60px] text-xs"
            rows={3}
          />
        </div>
      );
    }

    return (
      <div className={cn("relative", className)}>
        <Input
          ref={inputRef}
          type={inputType === 'number' ? 'number' : inputType === 'date' ? 'date' : inputType === 'url' ? 'url' : 'text'}
          value={localValue || ''}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="h-8 text-xs"
        />
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "py-2 px-3 cursor-pointer rounded-md transition-all duration-200 relative group min-h-[32px] flex items-center",
        "hover:bg-blue-50 dark:hover:bg-blue-900/20",
        "hover:shadow-sm hover:border-2 hover:border-blue-300 dark:hover:border-blue-600",
        "hover:scale-[1.02] hover:-translate-y-0.5",
        isPending && "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300",
        className
      )}
      onClick={handleClick}
    >
      {isPending && (
        <div className="absolute -top-1 -right-1 z-10">
          <Badge variant="secondary" className="h-4 px-1 text-xs bg-yellow-100 text-yellow-800">
            <Loader2 className="h-2 w-2 animate-spin mr-1" />
            Saving
          </Badge>
        </div>
      )}
      <div className="truncate flex-1" title={formatDisplayValue(value)}>
        {formatDisplayValue(value)}
      </div>
      {editable && (
        <div className="absolute inset-0 rounded-md border-2 border-transparent group-hover:border-blue-400 dark:group-hover:border-blue-500 group-hover:shadow-md transition-all duration-200 pointer-events-none" />
      )}
    </div>
  );
};
